import { useCallback, useEffect, useState } from 'react';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { items as itemsApi } from '@afosto/order/api/Items';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import uuid from '@afosto/utils/uuid';
import chunk from 'lodash.chunk';
import getServicePointAddress from './shipment/getServicePointAddress';
import fetchMultipleProducts from './fetchMultipleProducts';
import handleOrderItemDifferences from './handleOrderItemDifferences';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import isDefined from '@afosto/utils/isDefined';

const ORDER_PROCESSING_SETTINGS_PATH = 'afosto.tenant.odr.orders.processing';

export default (clientID, settings) => {
  const [billing, setBilling] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [calculation, setCalculation] = useState(null);
  const [client, setClient] = useState(null);
  const [contact, setContact] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [hasProjectionError, setHasProjectionError] = useState(false);
  const [items, setItems] = useState([]);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isFetchingProjection, setIsFetchingProjection] = useState(false);
  const [isCreatingCalculation, setIsCreatingCalculation] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [order, setOrder] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [projection, setProjection] = useState({});
  const [reference, setReference] = useState(null);
  const [shipping, setShipping] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);
  const [availableAddresses, setAvailableAddresses] = useState(null);
  const [itemAdjustments, setItemAdjustments] = useState({});
  const [orderAdjustments, setOrderAdjustments] = useState([]);
  const [itemPrices, setItemPrices] = useState({});
  const [pricingAt, setPricingAt] = useState(null);
  const [orderTags, setOrderTags] = useState([]);
  const { type: providedType } = settings || {};

  const createNewOrder = async type => {
    if (isCreatingOrder) {
      return Promise.reject(new Error('Already creating order'));
    }

    try {
      setIsCreatingOrder(true);

      const { data: processingSettings = {} } = await profileApi.getProfile(
        clientID
          ? ORDER_PROCESSING_SETTINGS_PATH.replace('tenant', clientID)
          : ORDER_PROCESSING_SETTINGS_PATH,
      );

      const response = await ordersApi.createStack({
        type,
        ...(clientID ? { client_id: clientID } : {}),
        processing: {
          confirm: settings?.processing?.confirm ?? processingSettings?.confirm,
          allocation: settings?.processing?.allocation ?? processingSettings?.allocation,
          fulfilment: settings?.processing?.fulfilment ?? processingSettings?.fulfilment,
          capture: settings?.processing?.capture ?? processingSettings?.capture,
        },
        settings: {
          billing: {
            method_code: settings?.billing?.method?.code ?? null,
            issuer_code: settings?.billing?.issuer?.code ?? null,
            provider_code: settings?.billing?.provider ?? null,
          },
        },
      });
      const { data = null } = response;

      setOrder(data);

      return data;
    } catch (err) {
      setOrder(null);

      return Promise.reject(err);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const updateOrder = async orderData => {
    try {
      let currentOrder = { ...order };
      let newOrderData = { ...orderData };

      if (!currentOrder?.id) {
        currentOrder = await createNewOrder(orderData?.type ?? settings?.type);
        newOrderData = {
          ...currentOrder,
          ...orderData,
          settings: {
            ...(currentOrder?.settings ?? {}),
            ...(orderData?.settings ?? {}),
          },
        };
      }

      const response = await ordersApi.updateOrderSettings(currentOrder?.id, newOrderData);
      const { data: updatedOrder } = response;

      setOrder(updatedOrder);

      return updatedOrder;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const changeReference = async event => {
    try {
      const newValue = event?.target?.value;

      if (order || (!order && newValue)) {
        await updateOrder({
          ...(order ?? {}),
          settings: {
            ...(order?.settings ?? {}),
            reference: newValue,
          },
        });
      }

      setReference(newValue);
    } catch (err) {
      setReference(null);
    }
  };

  const changeClient = async (ev, newValue) => {
    try {
      let clientProcessing = {};

      if (newValue) {
        const { data: processDefaultData = {} } = await profileApi.getProfile(
          ORDER_PROCESSING_SETTINGS_PATH.replace('tenant', newValue?.client_id),
        );
        clientProcessing = processDefaultData;
      }

      await updateOrder({
        ...(order ?? {}),
        client_id: newValue?.client_id,
        settings: {
          ...(order?.settings ?? {}),
          billing: {
            ...(order?.settings?.billing ?? {}),
            method_code: settings?.billing?.method?.code ?? null,
            issuer_code: settings?.billing?.issuer?.code ?? null,
            provider_code: settings?.billing?.provider ?? null,
          },
          shipping: {
            ...(order?.settings?.shipping ?? {}),
            method_id: null,
            spid: null,
            window: null,
          },
        },
        processing: {
          ...(order?.processing ?? {}),
          ...(clientProcessing ?? {}),
        },
      });

      setClient(newValue);
    } catch (err) {
      setClient(null);
    } finally {
      setBilling(null);
      setShipping(oldShipping => ({
        ...oldShipping,
        method: null,
        servicePoint: null,
      }));
    }
  };

  const fetchOrganisation = async organisationId => {
    try {
      const response = await organisationsApi.viewOrganisation(organisationId);
      const { data } = response;

      if (!data) {
        return null;
      }

      return { ...data, label: data?.name, value: data?.id };
    } catch (err) {
      return null;
    }
  };

  const changeContact = async (ev, newValue) => {
    try {
      if (contact?.id !== newValue?.id) {
        let primaryOrganisation = null;
        const filteredSecondaryOrganisations = newValue?.organisations?.secondary?.filter(
          item => !item.deleted_at,
        );

        if (newValue?.organisations?.primary?.id && !newValue?.organisations?.primary?.deleted_at) {
          primaryOrganisation = await fetchOrganisation(newValue.organisations.primary.id);
        } else if (filteredSecondaryOrganisations?.length > 0) {
          primaryOrganisation = await fetchOrganisation(filteredSecondaryOrganisations[0]?.id);
        }

        const organisationBillingAddress = primaryOrganisation?.billing?.primary;
        const organisationBillingAddressId = organisationBillingAddress?.id;

        const organisationshippingAddress = primaryOrganisation?.shipping?.primary;
        const organisationshippingAddressId = organisationshippingAddress?.id;

        setAvailableAddresses({
          contact: { shipping: newValue?.shipping, billing: newValue?.billing },
          organisation: {
            shipping: primaryOrganisation?.shipping,
            billing: primaryOrganisation?.billing,
          },
        });

        const billingAddressId = organisationBillingAddressId ?? newValue?.billing?.primary?.id;
        setBillingAddress(organisationBillingAddress ?? newValue?.billing?.primary ?? null);

        const shippingAddressId = organisationshippingAddressId ?? newValue?.shipping?.primary?.id;
        setShippingAddress(organisationshippingAddress ?? newValue?.shipping?.primary ?? null);

        await updateOrder({
          ...(order ?? {}),
          settings: {
            ...(order?.settings ?? {}),
            contact: {
              id: newValue?.value ?? null,
            },
            organisation: {
              id: primaryOrganisation?.id ?? null,
            },
            billing: {
              ...(order?.settings?.billing ?? {}),
              address_id: billingAddressId,
            },
            shipping: {
              ...(order?.settings?.shipping ?? {}),
              address_id: shippingAddressId,
            },
          },
        });

        setContact(newValue);
        setOrganisation(primaryOrganisation);
      }
    } catch (err) {
      setContact(null);
      setOrganisation(null);
    }
  };

  const updateContact = (ev, newValue) => {
    setContact(newValue);
    setAvailableAddresses(oldAvailableAddresses => ({
      ...oldAvailableAddresses,
      contact: { shipping: newValue?.shipping, billing: newValue?.billing },
    }));
  };

  const changeOrganisation = async (ev, newValue) => {
    try {
      if (organisation?.id !== newValue?.id) {
        const primaryBillingAddress = newValue?.billing?.primary;
        const primaryShippingingAddress = newValue?.shipping?.primary;

        setAvailableAddresses({
          ...availableAddresses,
          organisation: {
            shipping: newValue?.shipping,
            billing: newValue?.billing,
          },
        });

        const billingAddressId = primaryBillingAddress?.id ?? contact?.billing?.primary?.id ?? null;
        setBillingAddress(primaryBillingAddress ?? contact?.billing?.primary ?? null);

        const shippingAddressId =
          primaryShippingingAddress?.id ?? contact?.shipping?.primary?.id ?? null;
        setShippingAddress(primaryShippingingAddress ?? contact?.shipping?.primary ?? null);

        await updateOrder({
          ...(order ?? {}),
          settings: {
            ...(order?.settings ?? {}),
            organisation: {
              id: newValue?.value,
            },
            billing: {
              ...(order?.settings?.billing ?? {}),
              address_id: billingAddressId,
            },
            shipping: {
              ...(order?.settings?.shipping ?? {}),
              address_id: shippingAddressId,
            },
          },
        });

        setOrganisation(newValue);
      }
    } catch (err) {
      setOrganisation(null);
    }
  };

  const updateOrganisation = (ev, newValue) => {
    setOrganisation(newValue);
    setAvailableAddresses(oldAvailableAddresses => ({
      ...oldAvailableAddresses,
      organisation: { shipping: newValue?.shipping, billing: newValue?.billing },
    }));
  };

  const changeBillingAddress = async (ev, newValue) => {
    try {
      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          billing: {
            ...(order?.settings?.billing ?? {}),
            address_id: newValue?.id ?? null,
          },
        },
      });

      setBillingAddress(newValue);
    } catch (err) {
      setBillingAddress(null);
    }
  };

  const changeShippingAddress = async (ev, newValue) => {
    try {
      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          shipping: {
            ...(order?.settings?.shipping ?? {}),
            address_id: newValue?.id ?? null,
          },
        },
      });

      setShippingAddress(newValue);
    } catch (err) {
      setShippingAddress(null);
    }
  };

  const changeShipping = async newValue => {
    try {
      const carrierLocationAddressID = await getServicePointAddress(newValue);

      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          due_at: newValue.deliveryAt,
          shipping: {
            ...(order?.settings?.shipping ?? {}),
            address_id: carrierLocationAddressID ?? shippingAddress?.id ?? null,
            method_id: newValue?.method?.id ?? null,
            spid: newValue?.servicePoint?.id ?? null,
            is_split_allowed: newValue?.isSplitAllowed,
            window: newValue?.deliveryAt
              ? {
                  delivery_at: newValue?.deliveryAt,
                }
              : null,
          },
        },
      });

      setShipping(newValue);
    } catch (err) {
      setShipping(null);
    }
  };

  const changeBilling = async newValue => {
    try {
      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          billing: {
            ...(order?.settings?.billing ?? {}),
            method_code: newValue?.method?.code ?? null,
            issuer_code: newValue?.issuer?.code ?? null,
            provider_code: newValue?.provider ?? null,
          },
        },
      });
      setBilling(newValue);
    } catch (err) {
      setBilling(null);
    }
  };

  const changeOrderTags = async (ev, newValue) => {
    try {
      if (order || (!order && newValue)) {
        await updateOrder({
          ...(order ?? {}),
          tags: newValue.reduce((acc, value) => [...acc, value?.code], []),
        });
      }

      setOrderTags(newValue);
    } catch (err) {
      setOrderTags([]);
    }
  };

  const addCoupon = async coupon => {
    try {
      const couponExists = (order?.settings?.coupons ?? []).some(code => code === coupon);

      if (couponExists) {
        return order.settings.coupons;
      }

      const newCoupons = [...new Set([...(order?.settings?.coupons ?? []), coupon])];

      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          coupons: newCoupons,
        },
      });

      setCoupons(newCoupons);

      return newCoupons;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const removeCoupon = async coupon => {
    try {
      const newCoupons = (order?.settings?.coupons ?? []).filter(code => code !== coupon);

      await updateOrder({
        ...(order ?? {}),
        settings: {
          ...(order?.settings ?? {}),
          coupons: newCoupons,
        },
      });

      setCoupons(newCoupons);

      return newCoupons;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const createItem = async ({ sku, description, quantity, isPurchase, orderType = 'order' }) => {
    try {
      const formattedQuantity = parseInt(quantity, 10);
      let isInbound = formattedQuantity < 0 || isPurchase;
      let currentOrder = order;

      if (!currentOrder?.id) {
        currentOrder = await createNewOrder(orderType);
      }

      if (isPurchase) {
        isInbound = true;
      }

      const item = {
        sku,
        reference: uuid(),
        description,
        quantity: Math.abs(formattedQuantity),
        is_inbound: isInbound,
      };

      await itemsApi.addItems(currentOrder?.id, [item]);
      return item;
    } catch (err) {
      return Promise.resolve(err);
    }
  };

  const bulkCreateItem = async ({ data, isPurchase, orderType = 'order' }) => {
    try {
      let currentOrder = order;

      if (!currentOrder?.id) {
        currentOrder = await createNewOrder(orderType);
      }

      const bulkItems = data.reduce((acc, row) => {
        const [sku, quantity] = row ? Object.values(row) : [];
        const formattedQuantity = isDefined(quantity) ? parseInt(quantity, 10) : 0;
        const isInbound = formattedQuantity < 0 || isPurchase;

        const item = {
          sku,
          reference: uuid(),
          quantity: Math.abs(formattedQuantity),
          is_inbound: isInbound,
        };

        return [...acc, item];
      }, []);

      const chunkedSkus = chunk(
        bulkItems.map(item => item.sku),
        250,
      );
      const promises = chunkedSkus.map(skus =>
        fetchMultipleProducts(
          skus,
          {},
          {
            headers: {
              'x-page-size': 250,
            },
          },
        ),
      );
      const results = await Promise.allSettled(promises).catch(() => []);
      const products = results
        .filter(result => result.status === 'fulfilled')
        .reduce((acc, result) => [...acc, ...result.value], [])
        .reduce((acc, product) => ({ ...acc, [product?.sku]: product }), {});

      const formattedItems = bulkItems.reduce((acc, item) => {
        const product = products[item.sku];
        return [...acc, { ...item, description: product?.name }];
      }, []);

      await itemsApi.addItems(currentOrder?.id, formattedItems);
      return formattedItems;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const removeItems = async (selectedItems, onRemovedItems) => {
    try {
      const itemIds = selectedItems.reduce(
        (acc, selectedItem) => [...acc, ...(selectedItem?.ids ?? [])],
        [],
      );

      if (itemIds.length > 0) {
        await itemsApi.deleteItems(order?.id, itemIds);
        if (onRemovedItems && typeof onRemovedItems === 'function') {
          onRemovedItems();
        }
      }

      return itemIds;
    } catch (err) {
      return Promise.reject();
    }
  };

  const changeItemQuantity = async ({ data, item, value, isReturn }) => {
    try {
      const currentItem = findObjectInArray(data, 'reference', item?.reference);
      const currentItems = { [currentItem?.reference]: currentItem };
      const formattedQuantity = parseInt(value, 10);
      let newQuantity = !Number.isNaN(formattedQuantity) ? formattedQuantity : item?.quantity ?? 1;

      if (isReturn) {
        newQuantity = Math.abs(newQuantity) * -1;
      }

      const newItem = { ...item, quantity: newQuantity };
      await handleOrderItemDifferences(order, [newItem], currentItems);

      return newItem;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const createCalculation = async () => {
    try {
      const formattedItems = (items ?? []).reduce((acc, item = {}) => {
        const formattedAdjustments = (item?.adjustments ?? [])
          .filter(adjustment => !adjustment?.result?.is_applied_rule)
          .reduce(
            (adjustmentAcc, adjustment) => [
              ...adjustmentAcc,
              {
                amount: adjustment?.amount,
                reference: adjustment?.reference,
                description: adjustment?.description,
                is_percentage: adjustment?.is_percentage,
                is_discount: adjustment?.is_discount,
              },
            ],
            [],
          );
        const [itemReference] = Object.keys(itemPrices ?? {}).filter(itemPrice =>
          (item?.ids ?? []).includes(itemPrice),
        );
        const [firstID] = item?.ids ?? [];
        return [
          ...acc,
          {
            item_ids: item?.ids,
            description: item?.description,
            amount: itemPrices[itemReference] ?? null,
            reference: firstID,
            is_discounted: item?.is_discounted,
            adjustments: formattedAdjustments,
            vat_percentage: item?.vat_percentage,
          },
        ];
      }, []);

      if (order?.id) {
        const response = await ordersApi.createCalculation(order.id, {
          currency: projection?.currency,
          items: formattedItems,
          adjustments: (projection?.adjustments ?? []).filter(
            adjustment => !adjustment?.result?.is_applied_rule,
          ),
          pricing_at: projection?.pricing_at,
        });
        const { data } = response;
        await setCalculation(data);
        return data;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const addItemAdjustment = (sku, adjustment) => {
    setItemAdjustments({
      ...itemAdjustments,
      [sku]: [
        ...(itemAdjustments[sku] ?? []).filter(item => item?.reference !== adjustment?.reference),
        adjustment,
      ],
    });
  };

  const removeItemAdjustment = (sku, adjustmentReference) => {
    setItemAdjustments({
      ...itemAdjustments,
      [sku]: [
        ...(itemAdjustments[sku] || []).filter(
          adjustment => adjustment?.reference !== adjustmentReference,
        ),
      ],
    });
  };

  const addOrderAdjustment = adjustment => {
    setOrderAdjustments([adjustment]);
  };

  const removeOrderAdjustment = adjustmentReference => {
    setOrderAdjustments(orderAdjustments?.filter(item => item?.reference !== adjustmentReference));
  };

  const setItemPrice = (sku, price) => {
    setItemPrices({
      ...itemPrices,
      [sku]: price,
    });
  };

  const removeItemPrice = sku => {
    const newItemPrices = { ...itemPrices };
    delete newItemPrices[sku];
    setItemPrices(newItemPrices);
  };

  const resetState = useCallback(() => {
    setBilling(null);
    setBillingAddress(null);
    setClient(null);
    setContact(null);
    setCoupons([]);
    setHasProjectionError(false);
    setItems([]);
    setItemPrices({});
    setIsCreatingOrder(false);
    setIsFetchingProjection(false);
    setIsLoadingOrder(true);
    setOrder(null);
    setOrganisation(null);
    setProjection({});
    setReference(null);
    setShipping(null);
    setShippingAddress(null);
    setAvailableAddresses(null);
    setOrderAdjustments([]);
    setOrderTags([]);
    setItemAdjustments([]);
  }, []);

  useEffect(() => {
    if (providedType) {
      resetState();
    }
  }, [providedType, resetState]);

  return {
    addCoupon,
    addItemAdjustment,
    addOrderAdjustment,
    availableAddresses,
    billing,
    billingAddress,
    bulkCreateItem,
    calculation,
    changeBilling,
    changeBillingAddress,
    changeClient,
    changeContact,
    changeItemQuantity,
    changeOrderTags,
    changeOrganisation,
    changeReference,
    changeShipping,
    changeShippingAddress,
    client,
    contact,
    coupons,
    createCalculation,
    createItem,
    createNewOrder,
    itemAdjustments,
    orderAdjustments,
    hasProjectionError,
    isCreatingOrder,
    isCreatingCalculation,
    isFetchingProjection,
    isLoadingOrder,
    items,
    itemPrices,
    order,
    orderTags,
    organisation,
    pricingAt,
    projection,
    reference,
    removeCoupon,
    removeItemPrice,
    removeItemAdjustment,
    removeItems,
    removeOrderAdjustment,
    resetState,
    shipping,
    shippingAddress,
    setAvailableAddresses,
    setBilling,
    setBillingAddress,
    setCalculation,
    setClient,
    setContact,
    setCoupons,
    setItemPrice,
    setItemPrices,
    setItemAdjustments,
    setOrderAdjustments,
    setHasProjectionError,
    setIsFetchingProjection,
    setIsCreatingCalculation,
    setIsLoadingOrder,
    setItems,
    setOrder,
    setOrderTags,
    setOrganisation,
    setPricingAt,
    setProjection,
    setReference,
    setShipping,
    setShippingAddress,
    updateContact,
    updateOrder,
    updateOrganisation,
  };
};
